import React from "react";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { useI18n } from "@/coquille-bridge";
import {
  ClassesListPanel,
  EventsListPanel,
  LightAttendancePanel,
  StudentScheduleSummary,
  ParentScheduleSummary,
  useScheduleScope,
} from "@/components/schedule/KarateSchedulePanels.jsx";

function useScheduleLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

// ——— Admin ———

export function AdminClassesPage() {
  const locale = useScheduleLocale();
  return (
    <AdminGuard>
      <PageSection
        title={locale === "en" ? "Classes / courses" : "Classes / cours"}
        subtitle={
          locale === "en"
            ? "Weekly schedule — demo data, official hours pending."
            : "Horaires hebdomadaires — données démo, horaires officiels en attente."
        }
        testId="page-admin-classes"
      >
        <ClassesListPanel allowCrud scope={{ mode: "admin" }} />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminEventsPage() {
  const locale = useScheduleLocale();
  return (
    <AdminGuard>
      <PageSection
        title={locale === "en" ? "Events" : "Événements"}
        subtitle={
          locale === "en"
            ? "Seminars, camps, demos — exams/competitions not operational."
            : "Séminaires, camps, démos — examens/compétitions non opérationnels."
        }
        testId="page-admin-evenements"
      >
        <EventsListPanel allowCrud scope={{ mode: "admin" }} />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminAttendancePage() {
  const locale = useScheduleLocale();
  return (
    <AdminGuard>
      <PageSection
        title={locale === "en" ? "Light attendance" : "Présences légères"}
        subtitle={
          locale === "en"
            ? "Phase 1 demo — no advanced statistics."
            : "Démo Phase 1 — pas de statistiques avancées."
        }
        testId="page-admin-presences"
      >
        <LightAttendancePanel scope={{ mode: "admin" }} canEdit />
      </PageSection>
    </AdminGuard>
  );
}

// ——— Sensei ———

export function SenseiClassesPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "My classes" : "Mes classes"}
      subtitle={locale === "en" ? "Your assigned courses — demo" : "Vos cours assignés — démo"}
      testId="page-sensei-classes"
    >
      <ClassesListPanel scope={scope} />
    </PageSection>
  );
}

export function SenseiEventsPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Events" : "Événements"}
      subtitle={locale === "en" ? "Upcoming activities — demo" : "Prochaines activités — démo"}
      testId="page-sensei-evenements"
    >
      <EventsListPanel scope={scope} />
    </PageSection>
  );
}

export function SenseiAttendancePage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Light attendance" : "Présences légères"}
      subtitle={
        locale === "en"
          ? "Mark attendance for your classes — local mock."
          : "Marquer les présences de vos cours — mock local."
      }
      testId="page-sensei-presences"
    >
      <LightAttendancePanel scope={scope} canEdit />
    </PageSection>
  );
}

// ——— Sempai ———

export function SempaiClassesPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Assisted classes" : "Classes assistées"}
      subtitle={
        locale === "en"
          ? "Assistant role — limited access vs Sensei."
          : "Rôle assistant — accès limité vs Sensei."
      }
      testId="page-sempai-classes"
    >
      <ClassesListPanel scope={scope} />
    </PageSection>
  );
}

export function SempaiEventsPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Events (assist)" : "Événements (aide)"}
      testId="page-sempai-evenements"
    >
      <EventsListPanel scope={scope} />
    </PageSection>
  );
}

export function SempaiAttendancePage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Light attendance" : "Présences légères"}
      subtitle={
        locale === "en"
          ? "You may mark attendance for assisted classes only."
          : "Vous pouvez marquer les présences des classes assistées uniquement."
      }
      testId="page-sempai-presences"
    >
      <LightAttendancePanel scope={scope} canEdit />
    </PageSection>
  );
}

// ——— Étudiant ———

export function MesCoursPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "My classes" : "Mes cours"}
      subtitle={locale === "en" ? "Your group and schedule — Phase 1 demo" : "Votre groupe et horaire — démo Phase 1"}
      testId="page-mes-cours"
    >
      <StudentScheduleSummary />
      <ClassesListPanel scope={scope} testId="student-classes-list" />
    </PageSection>
  );
}

export function MesEvenementsPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "My events" : "Mes événements"}
      subtitle={locale === "en" ? "Upcoming dojo events — demo" : "Événements à venir — démo"}
      testId="page-mes-evenements"
    >
      <EventsListPanel scope={scope} testId="student-events-list" />
    </PageSection>
  );
}

// ——— Parent ———

export function ParentSchedulePage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Schedules" : "Horaires"}
      subtitle={
        locale === "en"
          ? "Your children's classes — payments not yet managed."
          : "Cours de vos enfants — paiements non gérés."
      }
      testId="page-parent-horaires"
    >
      <ParentScheduleSummary />
      <ClassesListPanel scope={scope} testId="parent-classes-list" />
    </PageSection>
  );
}

export function ParentEventsPage() {
  const locale = useScheduleLocale();
  const scope = useScheduleScope();
  return (
    <PageSection
      title={locale === "en" ? "Events" : "Événements"}
      subtitle={
        locale === "en"
          ? "Upcoming events — payments not managed in Phase 1."
          : "Événements à venir — paiements non gérés en Phase 1."
      }
      testId="page-parent-evenements"
    >
      <EventsListPanel scope={scope} testId="parent-events-list" />
    </PageSection>
  );
}
